// Bottom-left rolling dialogue feed.
// Each line is team-coloured with the speaker's name + a small "● LIVE" or
// "○ FB" tag indicating live DeepSeek vs fallback line origin.

import type { DialogueLine } from '../ai/Dialogue';

const SOURCE_BADGE = (source: DialogueLine['source']) =>
  source === 'live' ? '<span style="color:#7eea88;font-size:9px;">● LIVE</span>'
                    : '<span style="color:#aaa;font-size:9px;">○ FB</span>';

const TEAM_COLOR = (team: 'blue' | 'red') => team === 'blue' ? '#88c8ff' : '#ff948a';

export class DialogueFeed {
  root: HTMLDivElement;

  constructor() {
    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed', bottom: '70px', left: '14px', zIndex: '45',
      display: 'flex', flexDirection: 'column', gap: '4px',
      maxWidth: '46vw', maxHeight: '40vh', overflow: 'hidden',
      pointerEvents: 'none', font: '12px/1.4 -apple-system, "PingFang SC", sans-serif',
    } as CSSStyleDeclaration);
    document.body.appendChild(this.root);
  }

  push(line: DialogueLine, displayName?: string): void {
    const el = document.createElement('div');
    Object.assign(el.style, {
      background: 'rgba(8,12,28,0.7)', border: `1px solid ${TEAM_COLOR(line.team)}40`,
      padding: '5px 10px', borderRadius: '6px',
      color: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
      transform: 'translateY(8px)', opacity: '0', transition: 'transform .25s, opacity .35s',
    } as CSSStyleDeclaration);
    const name = displayName ?? line.callsign;
    el.innerHTML = `
      <span style="color:${TEAM_COLOR(line.team)};font-weight:600">${escapeHtml(name)}</span>
      <span style="color:#666">·</span>
      ${SOURCE_BADGE(line.source)}
      <div style="color:#eee;margin-top:1px">${escapeHtml(line.text)}</div>
    `;
    this.root.appendChild(el);
    requestAnimationFrame(() => {
      el.style.transform = 'translateY(0)';
      el.style.opacity = '1';
    });
    setTimeout(() => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(-8px)';
      setTimeout(() => el.remove(), 400);
    }, 8000);
    // Cap visible count so UI does not stack indefinitely.
    while (this.root.children.length > 8) this.root.removeChild(this.root.firstChild!);
  }

  destroy(): void { this.root.remove(); }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
